import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { ChevronDownIcon, LogoutIcon } from './icons';

type Props = {
  name: string;
  email: string;
  role?: string;
  onSignOut?: () => void;
};

export function UserMenu({ name, email, role, onSignOut }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const initials = name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const hasDistinctName = Boolean(
    name &&
    email &&
    name.trim().toLowerCase() !== email.trim().toLowerCase() &&
    name.trim().toLowerCase() !== 'admin'
  );

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-full p-1 hover:bg-gray-50"
      >
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-100 text-xs font-semibold text-brand-700">
          {initials || 'A'}
        </span>
        <span className="hidden text-left sm:block">
          {role ? (
            <span className="block text-sm font-semibold text-brand-700">{role}</span>
          ) : hasDistinctName ? (
            <span className="block text-sm font-medium text-gray-900">{name}</span>
          ) : null}
          <span className={cn("block text-xs", role || hasDistinctName ? "text-gray-500" : "font-medium text-gray-900")}>
            {email}
          </span>
        </span>
        <ChevronDownIcon className="h-4 w-4 text-gray-500" />
      </button>
      {open ? (
        <div
          className={cn(
            'absolute right-0 top-full z-20 mt-2 w-56 overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg',
          )}
        >
          <div className="border-b border-gray-100 px-3 py-2 sm:hidden bg-gray-50/50">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Account</div>
            {role && <div className="mt-0.5 text-xs font-semibold text-brand-700 truncate">{role}</div>}
            {hasDistinctName && (
              <div className="mt-0.5 font-medium text-gray-900 truncate" title={name}>{name}</div>
            )}
            {email && (
              <div className={cn("text-xs truncate", hasDistinctName ? "text-gray-500" : "mt-0.5 font-medium text-gray-800")} title={email}>
                {email}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onSignOut?.();
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-800 hover:bg-gray-50"
          >
            <LogoutIcon className="h-4 w-4" /> Sign out
          </button>
        </div>
      ) : null}

    </div>
  );
}
